import React, { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BreadcrumbGroup, {
  BreadcrumbGroupProps,
} from "@amzn/awsui-components-react/polaris/breadcrumb-group";

import { TAB_ID_KEY, getValueFromUrlParameter } from "../../util/urlUtils";
import {
  LEFT_NAV_LABELS,
  LeftNavPath,
  resolveLeftNavPath,
  ValidationTabId,
} from "./shellRoutes";

/** App Shell breadcrumbs. Three-item trail on `/validation` (root + Validation + tab label), two-item elsewhere. */

const ROOT_BREADCRUMB_TEXT = "Devices ASIN Central";
const ROOT_BREADCRUMB_HREF = "/";
const VALIDATION_PATH: LeftNavPath = "/validation";

/** `dashboard` -> "Validation" (not "Dashboard"). */
const VALIDATION_TAB_LABELS: Readonly<Record<ValidationTabId, string>> = {
  dashboard: "Validation",
  "step-breakdown": "Validation Step Breakdown",
  "program-tickets": "Program Tickets",
  "catalog-issues": "Catalog Issues",
};

const DEFAULT_VALIDATION_TAB_ID: ValidationTabId = "dashboard";

/** Keeps indexing well-typed when `useMemo` generics get erased. */
function labelForLeftNavPath(path: LeftNavPath): string {
  return LEFT_NAV_LABELS[path];
}

/** Narrow a raw URL param value to a known `ValidationTabId`. */
function resolveValidationTabId(raw: string): ValidationTabId {
  switch (raw) {
    case "dashboard":
    case "step-breakdown":
    case "program-tickets":
    case "catalog-issues":
      return raw;
    default:
      return DEFAULT_VALIDATION_TAB_ID;
  }
}

export const AppBreadcrumbs: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const activePath = useMemo<LeftNavPath>(
    () => resolveLeftNavPath(location.pathname),
    [location.pathname]
  );

  // `location.search` dep: `getValueFromUrlParameter` reads
  // `window.location.search` directly, so recompute on Phase 2 tab switches.
  const items = useMemo<ReadonlyArray<BreadcrumbGroupProps.Item>>(() => {
    const rootItem: BreadcrumbGroupProps.Item = {
      text: ROOT_BREADCRUMB_TEXT,
      href: ROOT_BREADCRUMB_HREF,
    };

    const secondItem: BreadcrumbGroupProps.Item = {
      text: labelForLeftNavPath(activePath),
      href: activePath,
    };

    if (activePath !== VALIDATION_PATH) {
      return [rootItem, secondItem];
    }

    const tabId = resolveValidationTabId(
      getValueFromUrlParameter(TAB_ID_KEY, DEFAULT_VALIDATION_TAB_ID)
    );

    const thirdItem: BreadcrumbGroupProps.Item = {
      text: VALIDATION_TAB_LABELS[tabId],
      href: VALIDATION_PATH,
    };

    return [rootItem, secondItem, thirdItem];
  }, [activePath, location.search]);

  const handleFollow = useCallback(
    (
      event: CustomEvent<
        BreadcrumbGroupProps.ClickDetail<BreadcrumbGroupProps.Item>
      >
    ) => {
      event.preventDefault();
      // Preserve `search` and `hash` (the serialized Applied Filter lives in the hash).
      navigate({
        pathname: event.detail.href,
        search: window.location.search,
        hash: window.location.hash,
      });
    },
    [navigate]
  );

  return (
    <BreadcrumbGroup
      // Spread to satisfy Cloudscape's mutable-array typing.
      items={[...items]}
      onFollow={handleFollow}
      expandAriaLabel="Show path"
      ariaLabel="Breadcrumbs"
    />
  );
};

export default AppBreadcrumbs;
